import {uploadResumable,LEGACY_MAX_MEDIA_BYTES,MAX_MEDIA_BYTES} from './resumableUpload.js';
import {uploadR2,r2SignedUrl,isR2Row,r2Request} from './r2Media.js';
export const MEDIA_BUCKET='cutscript-media';
export const projectSnapshot=project=>JSON.parse(JSON.stringify(project));
export function createProjectStore(client,{uploadMedia=uploadResumable,uploadR2Media=uploadR2,useR2=typeof window!=='undefined'&&import.meta.env?.VITE_USE_R2_MEDIA!=='false'}={}) {
 const bucket=()=>client.storage.from(MEDIA_BUCKET);
 const checked=({data,error})=>{if(error) throw error; return data;};
 // Deletion is two-phase: reserve in Postgres, remove with the Storage API,
 // then finalize. Pending assets are retried if connectivity fails.
 const finishMediaCleanup=async paths=>{
  // R2 media is not in Supabase Storage. Identify its immutable upload record
  // through the owner-only Library and delete via the authenticated R2 API.
  const r2Assets=new Map();
  if(useR2&&paths.length){
   for(let i=0;i<paths.length;i+=100){
    const matches=checked(await client.from('cutscript_media_library')
     .select('media_path,data,deletion_pending').in('media_path',paths.slice(i,i+100)));
    for(const asset of matches||[])if(asset.deletion_pending&&isR2Row(asset))r2Assets.set(asset.media_path,asset);
   }
  }
  for(const [path,asset] of r2Assets){
   await r2Request(client,{action:'delete',id:asset.data.r2UploadId});
   checked(await client.rpc('cutscript_media_finalize_deletion',{p_paths:[path]}));
  }
  paths=paths.filter(path=>!r2Assets.has(path));
  for(let i=0;i<paths.length;i+=100){
   const batch=paths.slice(i,i+100);
   checked(await bucket().remove(batch));
   // Storage can respond successfully even when an RLS filter leaves an
   // object untouched. Check that EACH file is absent before finalizing.
   for(const path of batch){
    const cut=path.lastIndexOf('/');
    if(cut<0)throw new Error('Unexpected media path.');
    const folder=path.slice(0,cut),name=path.slice(cut+1);
    const objects=checked(await bucket().list(folder,{limit:100}));
    if(objects.some(object=>object.name===name))
     throw new Error('The storage service did not remove every file. Cleanup will be retried.');
   }
   checked(await client.rpc('cutscript_media_finalize_deletion',{p_paths:batch}));
  }
 };
 return {
  async list(){return checked(await client.from('cutscript_projects').select('id,data,revision,media_path,media_ready,media_bytes,source_detached,updated_at,deleted_at').is('deleted_at',null).order('updated_at',{ascending:false}));},
  async listTrash(){return checked(await client.from('cutscript_projects')
    .select('id,data,revision,source_detached,updated_at,deleted_at')
    .not('deleted_at','is',null).order('deleted_at',{ascending:false}));},
  async get(id){return checked(await client.from('cutscript_projects').select('*').eq('id',id).single());},
  async create(ownerId,file,project,{onProgress}={}){
   if(!file||file.size<1||file.size>MAX_MEDIA_BYTES)throw new Error('Select an MP4 or WebM interview up to 5 GB.');
   // Reuse an incomplete upload when its original source matches exactly.
   const pending=checked(await client.from('cutscript_projects').select('*').eq('owner_id',ownerId).eq('media_ready',false));
   let row=pending.find(item=>item.media_bytes===file.size&&item.data?.filename===project.filename&&item.data?.collectionId===project.collectionId&&(!item.data?.uploadModified||item.data.uploadModified===file.lastModified));
   const resuming=Boolean(row);
   const r2=row?row.data?.mediaProvider==='r2':useR2;
   if(!r2&&file.size>LEGACY_MAX_MEDIA_BYTES)throw new Error('The old storage route allows 50 MB. R2 is required for larger uploads.');
   if(!row){
    const id=crypto.randomUUID(),media_path=ownerId+'/'+id+'/source';
    const data={...projectSnapshot(project),...(r2?{mediaProvider:'r2'}:{})};
    if(r2)delete data.r2UploadId;
    row=checked(await client.from('cutscript_projects').insert({id,owner_id:ownerId,media_path,media_bytes:file.size,data}).select().single());
   }
   // A transfer may have fully reached Storage before the database update failed.
   // A completed source already exists as one object; do not re-upload or overwrite it.
   if(resuming&&r2&&row.data?.r2UploadId){
    try{
     await r2SignedUrl(client,row);
     onProgress?.(100);
     return checked(await client.from('cutscript_projects').update({media_ready:true}).eq('id',row.id).select().single());
    }catch{/* Previous R2 upload was not completed or is no longer accessible. */}
   }
   if(resuming&&!r2){
    const found=await bucket().list(ownerId+'/'+row.id,{limit:10});
    if(!found.error&&found.data?.some(object=>object.name==='source')){
     onProgress?.(100);
     return checked(await client.from('cutscript_projects').update({media_ready:true}).eq('id',row.id).select().single());
    }
   }
   // Interrupted transfers remain discoverable. Never delete a partial row on a network failure.
   if(r2){
    const uploaded=await uploadR2Media(client,file,row.id,onProgress);
    if(!uploaded?.id||uploaded?.status!=='ready'||uploaded?.provider!=='r2')
     throw new Error('R2 did not confirm this private upload.');
    // Complete upload and identity together; the DB guard verifies the ready
    // R2 object belongs to the current owner and matches the recorded bytes.
    return checked(await client.from('cutscript_projects')
     .update({media_ready:true,data:{...row.data,mediaProvider:'r2',r2UploadId:uploaded.id}})
     .eq('id',row.id).eq('media_ready',false).select().single());
   }
   await uploadMedia(client,ownerId,row.id,row.media_path,file,onProgress);
   return checked(await client.from('cutscript_projects').update({media_ready:true}).eq('id',row.id).select().single());
  },
  async save(row,project){
   // A UI save must never erase the immutable R2 pointer: it would make an
   // otherwise successful upload and all Media Library references unplayable.
   const snapshot=projectSnapshot(project);
   if(isR2Row(row)){snapshot.mediaProvider='r2';snapshot.r2UploadId=row.data.r2UploadId;}
   const data=checked(await client.from('cutscript_projects').update({data:snapshot}).eq('id',row.id).eq('revision',row.revision).select().maybeSingle());
   if(!data) throw new Error('This project changed in another tab. Reopen the cloud project before editing further. Your local edit has not overwritten it.');
   return data;
  },
  async url(row){return isR2Row(row)?r2SignedUrl(client,row):checked(await bucket().createSignedUrl(row.media_path,86400)).signedUrl;},
  async file(row){
   if(isR2Row(row)){
    const response=await fetch(await r2SignedUrl(client,row));
    if(!response.ok)throw new Error('R2 video download failed ('+response.status+').');
    const blob=await response.blob();
    return new File([blob],row.data.filename||'interview.mp4',{type:blob.type||'video/mp4'});
   }
   const blob=checked(await bucket().download(row.media_path));return new File([blob],row.data.filename,{type:blob.type});
  },
  async listMedia(){
   const rows=checked(await client.from('cutscript_media_library')
     .select('id,owner_id,media_path,media_bytes,data,deletion_pending,created_at,updated_at')
     .order('created_at',{ascending:false}));
   const pending=rows.filter(asset=>asset.deletion_pending).map(asset=>asset.media_path);
   if(pending.length)await finishMediaCleanup(pending).catch(()=>{ /* Retry on next refresh. */ });
   return rows.filter(asset=>!asset.deletion_pending);
  },
  async trashProject(projectId){
   return checked(await client.rpc('cutscript_project_trash',{p_project:projectId,p_action:'trash'}));
  },
  async restoreProject(projectId){
   return checked(await client.rpc('cutscript_project_trash',{p_project:projectId,p_action:'restore'}));
  },
  async previewMerge(sourceId,destinationId,appendAssembly=true){
   return checked(await client.rpc('cutscript_project_merge',{
    p_source:sourceId,p_destination:destinationId,p_append_assembly:appendAssembly,p_preview:true
   }));
  },
  async mergeProjects(sourceId,destinationId,appendAssembly=true){
   return checked(await client.rpc('cutscript_project_merge',{
    p_source:sourceId,p_destination:destinationId,p_append_assembly:appendAssembly,p_preview:false
   }));
  },
  async previewProjectDelete(projectId){
   return checked(await client.rpc('cutscript_project_delete',{
    p_project:projectId,p_delete_media:false,p_preview:true
   }));
  },
  async removeProject(projectId,deleteMedia=false){
   const result=checked(await client.rpc('cutscript_project_delete',{
    p_project:projectId,p_delete_media:Boolean(deleteMedia),p_preview:false
   }));
   const paths=deleteMedia?(result.files||[]).filter(file=>file.eligible).map(file=>file.path):[];
   if(paths.length){
    try{await finishMediaCleanup(paths);}
    catch(error){
     return {...result,cleanup_pending:true,cleanup_error:error.message||'Storage cleanup failed'};
    }
   }
   return {...result,cleanup_pending:false};
  },
  async detachRootInterview(projectId){
   return checked(await client.rpc('cutscript_root_interview_detach',{p_project:projectId}));
  },
  async reuseMedia(assetId,projectId=null){
   return checked(await client.rpc('cutscript_media_use',{p_asset:assetId,p_project:projectId}));
  },
  // Project deletion never removes a ready media file owned by the shared library.
  async remove(row){
   if(row.media_ready!==true)checked(await bucket().remove([row.media_path]));
   checked(await client.from('cutscript_projects').delete().eq('id',row.id));
  }
 };
}
